import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tooltip'
import { G, Image, Path, Svg, View } from '../../compiler'
import { isNumber } from '../../utils'
import AverageIcon from './AverageIcon'
import InitialsIcon from './InitialsIcon'
import UserDimensionValue from './UserDimensionValue'
import UserDimensionValueWithIcon from './UserDimensionValueWithIcon'

export const USER_ICON_SIZE = 16

const EXPERT_TOOLTIP_STYLE = {
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  fontWeight: '600',
  height: '2rem',
  backgroundColor: '#fff',
  alignItems: 'center',
  display: 'flex',
  opacity: 1,
  justifyContent: 'center',
  color: '#182033',
  borderRadius: 6,
  boxShadow:
    '0px 4px 3px 0px rgba(0,0,0,0.10), 0px 10px 8px 0px rgba(0,0,0,0.04)'
}

const BeamGraph = ({
  title,
  expertInputValues = [],
  teamInputValues = [],
  minYellow = 0,
  maxYellow = 0,
  minGreen = 0,
  maxGreen = 0,
  dimensionMatch = null,
  userDimensionValue = null
}) => {
  const { t } = useTranslation()
  const avg =
    teamInputValues?.reduce((sum, obj) => sum + obj.value, 0) /
    teamInputValues?.length

  const hasTeamInputValues = teamInputValues && teamInputValues.length > 0

  const instanceId = React.useId().replace(/:/g, '')
  const safeTitle = String(title || '')
    .toLowerCase()
    .replace(/\s+/g, '-')

  const idealAreaTooltipId = `ideal-area-tooltip-${safeTitle}-${instanceId}`
  const transitionAreaTooltipId = `transition-area-tooltip-${safeTitle}-${instanceId}`
  const populationAverageTooltipId = `population-average-tooltip-${safeTitle}-${instanceId}`
  const avgTooltipId = `average-icon-tooltip-${safeTitle}-${instanceId}`

  return (
    <View>
      <View
        style={{
          height: 8,
          width: '100%',
          borderRadius: 2,
          backgroundColor: '#EBEFFD',
          position: 'relative'
        }}
      >
        <View
          data-tooltip-content={t('global.transition_area')}
          data-tooltip-id={transitionAreaTooltipId}
          data-tooltip-place={minYellow < minGreen ? 'left' : 'right'}
          style={{
            backgroundColor: 'rgb(253,218,148)',
            height: 8,
            width: `${maxYellow - minYellow}%`,
            position: 'absolute',
            left: `${minYellow}%`
          }}
        />
        <Tooltip
          style={{ ...EXPERT_TOOLTIP_STYLE, color: '#FFB629' }}
          id={transitionAreaTooltipId}
          offset={12}
          opacity={1}
        />
        <View
          data-tooltip-content={t('global.ideal_area')}
          data-tooltip-id={idealAreaTooltipId}
          data-tooltip-place='top'
          style={{
            backgroundColor: 'rgb(167,247,231)',
            height: 8,
            width: `${maxGreen - minGreen}%`,
            position: 'absolute',
            left: `${minGreen}%`
          }}
        />
        <Tooltip
          style={{ ...EXPERT_TOOLTIP_STYLE, color: '#29CCC7' }}
          id={idealAreaTooltipId}
          offset={12}
          opacity={1}
        />

        {expertInputValues.map((value, index) => {
          const tooltipId = `user-icon-tooltip-${index}-${String(value)
            .toLowerCase()
            .replace(/\s+/g, '-')}`
          return (
            <React.Fragment key={index}>
              <Image
                data-tooltip-content={t('global.expert')}
                data-tooltip-id={tooltipId}
                data-tooltip-place='top'
                src={{
                  uri: 'https://aivy-assets.s3.eu-central-1.amazonaws.com/pdf_reports/images/user-icon.png'
                }}
                style={{
                  position: 'absolute',
                  top: -4,
                  height: USER_ICON_SIZE,
                  width: USER_ICON_SIZE,
                  transform: `translateX(-${USER_ICON_SIZE / 2}px)`,
                  left: `${value}%`
                }}
              />
              <Tooltip
                id={tooltipId}
                offset={8}
                style={EXPERT_TOOLTIP_STYLE}
                opacity={1}
              />
            </React.Fragment>
          )
        })}
        {/** teamInputValues */}
        {!!teamInputValues.length && (
          <React.Fragment>
            <AverageIcon
              bottom={4}
              left={`${avg}%`}
              tooltipId={avgTooltipId}
              tooltipContent={t('common.team_average')}
              tooltipPlace='top'
            />

            <Tooltip
              id={avgTooltipId}
              offset={16}
              opacity={1}
              style={EXPERT_TOOLTIP_STYLE}
            />
          </React.Fragment>
        )}
        {teamInputValues.map((member, index) => {
          const initialsTooltipId = `initials-icon-tooltip-${index}-${String(
            member.label || ''
          )
            .toLowerCase()
            .replace(/\s+/g, '-')}-${safeTitle}-${instanceId}`

          return (
            <React.Fragment key={index}>
              <InitialsIcon
                bottom={4}
                left={`${member.value}%`}
                label={member.label}
                tooltipId={initialsTooltipId}
                tooltipContent={member.label}
                tooltipPlace='top'
              />
              <Tooltip
                id={initialsTooltipId}
                style={EXPERT_TOOLTIP_STYLE}
                opacity={1}
              />
            </React.Fragment>
          )
        })}
        {isNumber(dimensionMatch) && isNumber(userDimensionValue) && (
          <UserDimensionValueWithIcon
            bottom={4}
            left={`${userDimensionValue}%`}
            fit={dimensionMatch}
          />
        )}
        {dimensionMatch === null && isNumber(userDimensionValue) && (
          <UserDimensionValue bottom={4} left={`${userDimensionValue}%`} />
        )}
        <React.Fragment>
          <View
            data-tooltip-content={t('global.population_average')}
            data-tooltip-id={populationAverageTooltipId}
            data-tooltip-place='top'
            style={
              hasTeamInputValues && {
                height: 16,
                width: 2,
                backgroundColor: '#849BB3'
              }
            }
            className='absolute -top-1 left-1/2 -translate-x-1/2'
          >
            {!hasTeamInputValues && (
              <Svg
                width='10'
                height='16'
                viewBox='0 0 10 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <G>
                  <Path
                    d='M0 4H10V12H0V4Z'
                    fill='#EBF0F5'
                    opacity={
                      minYellow || maxYellow || minGreen || maxGreen ? 0.5 : 1
                    }
                  />
                </G>
                <Path d='M4 0H6V16H4V0Z' fill='#849BB3' />
              </Svg>
            )}
          </View>
          <Tooltip
            id={populationAverageTooltipId}
            offset={12}
            style={{ ...EXPERT_TOOLTIP_STYLE, color: '#182033' }}
            opacity={1}
          />
        </React.Fragment>
      </View>
    </View>
  )
}

export default BeamGraph
